/**
 * @file ZapierProfileRow.tsx
 * @brief Profile → Connected apps → Zapier: how many Zapier connections there
 *        are, and the way in to Settings → Integrations (W29, Z1). Web and Mac.
 *
 * @details
 * The consent screen tells Zapier users they can disconnect "from Profile →
 * Connected apps", so the row lives here; the list with Disconnect is on the
 * Integrations page.
 *
 * M1 (29-10-2026): Make sits on the same row (both logos; the count covers
 * Zapier and Make connections).
 *
 * @author DES (prepared with Claude)
 * @date 27-10-2026
 */

import { Link } from "react-router-dom";
import { ChevronRight, Zap } from "lucide-react";
import { GridBand } from "@/features/analytics/components/GridBlock";
import { useAiConnections } from "@/features/mcp-connect/api";
import { useAuth } from "@/shared/auth/auth-context";
import { isCompanyAdminRole } from "@/shared/auth/homePath";
import { isAutomationKind } from "./zapierCopy";
import { MakeLogo } from "./MakeLogo";
import { ZapierWordmark } from "./ZapierWordmark";

export function integrationsPathFor(role: string | undefined): string {
  return isCompanyAdminRole(role) ? "/company/settings/integrations" : "/app/integrations";
}

export function ZapierProfileRow() {
  const { user, entitlements } = useAuth();
  const list = useAiConnections();
  const n = (list.data ?? []).filter((c) => isAutomationKind(c.kind)).length;
  const line = list.isLoading
    ? "Checking…"
    : n > 0
      ? `${n} connection${n === 1 ? "" : "s"}`
      : entitlements.zapier === false && entitlements.make === false
        ? "Not allowed by your company admin"
        : "Connect VERBLIZR to thousands of apps with Zapier or Make";

  return (
    <div className="border-t border-border/60">
      <GridBand className="text-sm font-medium">
        Integrations
        <span className="ml-1 font-normal text-muted-foreground">· Apps your meetings and notes can go to.</span>
      </GridBand>
      <Link
        to={integrationsPathFor(user?.role)}
        className="flex items-center gap-3 px-4 py-3.5 transition-colors hover:bg-muted/30 md:px-5"
      >
        <Zap className="h-5 w-5 shrink-0 text-brand" strokeWidth={1.75} />
        <span className="min-w-0 flex-1">
          <span className="my-0.5 flex items-center gap-3">
            <ZapierWordmark height={18} />
            <MakeLogo height={15} />
          </span>
          <span className="block truncate text-xs text-muted-foreground">{line}</span>
        </span>
        <span className="text-sm font-medium text-foreground">{n > 0 ? "Manage" : "Set up"}</span>
        <ChevronRight className="h-4 w-4 text-muted-foreground" />
      </Link>
    </div>
  );
}
